import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconClose } from '@toasttab/shared/src/assets/svg/icon-close.svg'

// About this component:
// Modal that opens after an engineer activates the ComponentInspectorToolbar and clicks a
// component on the page. Displays the React component's source file path and the Gatsby
// template used by the current page, each with "Open in VS Code" and clipboard copy actions.
// State is managed via ComponentInspectorProvider, which must wrap the app in gatsby-browser.tsx.

const backdropShellClassName =
  'fixed inset-0 bg-black/30 flex items-center justify-center z-[101] top-0 left-0 right-0 bottom-0'

interface ComponentInspectorModalProps {
  isOpen: boolean
  children: React.ReactNode
  onClose?: () => void
  contentClassName?: string
  contentStyle?: React.CSSProperties
  ariaLabel?: string
}

const ComponentInspectorModalShell = ({
  isOpen,
  children,
  onClose,
  contentClassName,
  contentStyle,
  ariaLabel = 'Dialog'
}: ComponentInspectorModalProps) => {
  useEffect(() => {
    if (!isOpen || !onClose) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || typeof document === 'undefined') return null

  return createPortal(
    <div className={backdropShellClassName} onClick={onClose}>
      <div
        aria-label={ariaLabel}
        aria-modal={true}
        className={classNames('z-[102]', contentClassName)}
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        style={contentStyle}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

export type ComponentInspect = {
  absolutePath: string
  lineNumber?: string
  columnNumber?: string
}

type ComponentInspectorContextValue = {
  setPageTemplate: (name: string) => void
  setComponentInspect: (info: ComponentInspect | null) => void
  openInspectorModal: () => void
  closeInspectorModal: () => void
}

const ComponentInspectorContext =
  createContext<ComponentInspectorContextValue | null>(null)

export function useComponentInspector(): ComponentInspectorContextValue {
  const ctx = useContext(ComponentInspectorContext)
  if (!ctx) {
    throw new Error(
      'useComponentInspector must be used within ComponentInspectorProvider'
    )
  }
  return ctx
}

/** Registers the active page template path (from wrapPageElement) for the component inspector modal. */
export function ComponentInspectorPageTemplateBridge({
  templateName
}: {
  templateName: string
}): null {
  const ctx = useContext(ComponentInspectorContext)
  useEffect(() => {
    ctx?.setPageTemplate(templateName)
  }, [templateName, ctx])
  return null
}

const panelSurfaceStyle: React.CSSProperties = {
  // Explicit dark surface — `bg-neutral-dark` in our Tailwind preset maps to a light bisque, not a dark panel.
  backgroundColor: '#252525',
  color: '#fff'
}

const modalContentClassName =
  'rounded-lg max-w-[min(90vw,40rem)] w-full mx-4 my-4 flex flex-col overflow-hidden p-6 shadow-xl'

const COPY_FLASH_MS = 1500

function ComponentInspectorModalView({
  isOpen,
  pageTemplate,
  component,
  onClose
}: {
  isOpen: boolean
  pageTemplate: string
  component: ComponentInspect | null
  onClose: () => void
}) {
  const [copyFlash, setCopyFlash] = useState<'template' | 'component' | null>(
    null
  )
  const copyFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flashCopied = (which: 'template' | 'component') => {
    if (copyFlashTimerRef.current) {
      clearTimeout(copyFlashTimerRef.current)
      copyFlashTimerRef.current = null
    }
    setCopyFlash(which)
    copyFlashTimerRef.current = setTimeout(() => {
      setCopyFlash(null)
      copyFlashTimerRef.current = null
    }, COPY_FLASH_MS)
  }

  useEffect(() => {
    if (!isOpen) {
      setCopyFlash(null)
      if (copyFlashTimerRef.current) {
        clearTimeout(copyFlashTimerRef.current)
        copyFlashTimerRef.current = null
      }
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (copyFlashTimerRef.current) clearTimeout(copyFlashTimerRef.current)
    }
  }, [])

  const openComponentIn = (scheme: string) => {
    if (!component) return
    const { absolutePath, lineNumber = '1', columnNumber = '1' } = component
    window.location.href = `${scheme}://file${absolutePath}:${lineNumber}:${columnNumber}`
    onClose()
  }

  const openTemplateIn = (ide: string) => {
    window.location.href = `${ide}://file${process.env.GATSBY_PACKAGES_MAIN_DIR}/${pageTemplate}`
  }

  const sitePackageRel = process.env.GATSBY_SITE_PACKAGE_REL
  const templateDisplayPath =
    pageTemplate && sitePackageRel
      ? `${sitePackageRel}/${pageTemplate}`
      : pageTemplate || '—'
  const componentDisplayPath = component
    ? `${component.absolutePath.replace(/^.*\/packages\//, 'packages/')}:${
        component.lineNumber
      }`
    : ''

  return (
    <ComponentInspectorModalShell
      ariaLabel='Component Inspector'
      contentClassName={modalContentClassName}
      contentStyle={panelSurfaceStyle}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className='mb-4 flex justify-end'>
        <IconClose
          className='h-6 w-6 cursor-pointer'
          fill='#a3a3a3'
          onClick={onClose}
        />
      </div>

      <section className='mb-6'>
        <h3 className='mb-2'>Component</h3>
        {component ? (
          <>
            <p className='mb-3 break-all font-mono text-sm text-white/80'>
              {componentDisplayPath}
            </p>
            <div className='flex flex-wrap gap-2'>
              <button
                className='rounded bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20'
                onClick={() => openComponentIn('vscode')}
                type='button'
              >
                Open in VS Code
              </button>
              <button
                className='min-w-[5.5rem] rounded bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20'
                title='Copy to Clipboard'
                type='button'
                onClick={() => {
                  void navigator.clipboard.writeText(componentDisplayPath)
                  flashCopied('component')
                }}
              >
                {copyFlash === 'component' ? 'Copied' : 'Copy to Clipboard'}
              </button>
            </div>
          </>
        ) : (
          <p className='text-sm text-white/50'>
            Inspect an element (⌃⇧⌘C) to see its source path here.
          </p>
        )}
      </section>

      <div className='border-t border-white/15 pt-6'>
        <h3 className='mb-2'>Template</h3>
        <p className='mb-3 break-all font-mono text-sm text-white/80'>
          {templateDisplayPath}
        </p>
        <div className='flex flex-wrap gap-2'>
          <button
            className='rounded bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40'
            disabled={!pageTemplate}
            onClick={() => openTemplateIn('vscode')}
            type='button'
          >
            Open in VS Code
          </button>
          <button
            className='min-w-[5.5rem] rounded bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40'
            disabled={!pageTemplate}
            title='Copy to Clipboard'
            type='button'
            onClick={() => {
              void navigator.clipboard.writeText(templateDisplayPath)
              flashCopied('template')
            }}
          >
            {copyFlash === 'template' ? 'Copied' : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
    </ComponentInspectorModalShell>
  )
}

export function ComponentInspectorProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [pageTemplate, setPageTemplate] = useState('')
  const [component, setComponentInspect] = useState<ComponentInspect | null>(
    null
  )
  const [isOpen, setIsOpen] = useState(false)

  const openInspectorModal = useCallback(() => setIsOpen(true), [])
  const closeInspectorModal = useCallback(() => {
    setIsOpen(false)
    setComponentInspect(null)
  }, [])

  const value = useMemo(
    () => ({
      setPageTemplate,
      setComponentInspect,
      openInspectorModal,
      closeInspectorModal
    }),
    [openInspectorModal, closeInspectorModal]
  )

  return (
    <ComponentInspectorContext.Provider value={value}>
      {children}
      <ComponentInspectorModalView
        component={component}
        isOpen={isOpen}
        onClose={closeInspectorModal}
        pageTemplate={pageTemplate}
      />
    </ComponentInspectorContext.Provider>
  )
}
